/**
 * KISSED University · Quiz Recommendation screen.
 * Route: /university/recommendation
 *
 * Reads quiz state from sessionStorage and renders:
 *  - Recommended program (L1..L4) OR the CE pathway
 *  - "Apply Now" → /university/apply with quiz context query params
 *  - "View Program Details" → /university/program/:key
 *  - "Retake Quiz" / "Choose a Different Pathway"
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, RefreshCcw, GraduationCap, Sparkles } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import {
  PROGRAMS, CE_COURSES,
  readQuizState, clearQuizState,
  QUIZ_TO_CATALOG_PRIMARY, mapCEFriendlyToCatalog,
} from "./quizData";

function buildApplyUrl({ quizKey, primaryProgram, ceFriendly, startMonth, quizRecommendedProgram }) {
  const params = new URLSearchParams();
  params.set("pathway_source", "admissions_quiz");
  params.set("quiz_key", quizKey);
  if (primaryProgram) params.set("primary_program", primaryProgram);
  if (quizRecommendedProgram) params.set("quiz_recommended_program", quizRecommendedProgram);
  if (startMonth) params.set("desired_start_month", startMonth);
  if (ceFriendly) params.set("ce_course_interest", ceFriendly);
  return `/university/apply?${params.toString()}`;
}

export default function UniversityRecommendation() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [ceChoice, setCeChoice] = useState("");

  useEffect(() => {
    const s = readQuizState();
    if (!s || !s.recommendation) {
      navigate("/university/quiz");
      return;
    }
    setState(s);
  }, [navigate]);

  if (!state) return null;

  // DTS-blocked notice: applicant scored toward L4 but lacks prerequisites and
  // was redirected to L3. Surface a clear explanation.
  const dtsRedirected = state.recommendation === "L3"
    && (state.answers?.q4 === "doctoral" || state.answers?.q2 === "doctoral")
    && (state.answers?.q1 === "none" || state.answers?.q1 === "foundational");

  const isCE = state.recommendation === "CE";
  const program = !isCE ? PROGRAMS[state.recommendation] : null;

  // Surface a one-time inline banner near the top of the recommendation card
  const dtsRedirectBanner = dtsRedirected ? (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 max-w-2xl mx-auto mb-6 text-left" data-testid="dts-redirect-banner">
      <div className="text-[11px] uppercase tracking-wider text-amber-900 font-semibold mb-2">Pathway Note</div>
      <p className="text-sm text-amber-900 leading-relaxed">
        The Doctor of Trichology Science pathway requires advanced trichology preparation or documented equivalent training. Based on your responses, we recommend beginning with the <b>Holistic Trichology Practitioner Certification</b> pathway first. This prepares you for future doctoral-level study.
      </p>
    </div>
  ) : null;
  const startMonth = state.desired_start_month || "";

  // Trichology recommendation (L1..L4)
  if (!isCE && program) {
    const catalogPrimary = QUIZ_TO_CATALOG_PRIMARY[program.key] || "";
    const quizRecommendedProgram = `${program.level} — ${program.title}`;
    const applyHref = buildApplyUrl({
      quizKey: program.key,
      primaryProgram: catalogPrimary,
      startMonth,
      quizRecommendedProgram,
    });
    const detailHref = `/university/program/${program.key}`;

    return (
      <RecommendationShell>
        <Hero
          eyebrow="Your recommended pathway"
          level={program.badge}
          title={program.title}
          desc={program.description}
        />
        <main className="max-w-3xl mx-auto px-6 py-10">
          {dtsRedirectBanner}
          <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="rec-info-grid">
            <InfoCell label="Program Level" value={program.level} />
            <InfoCell label="Clock Hours"   value={`${program.hours} Hours`} />
            <InfoCell label="Tuition"       value={program.tuition} />
            <InfoCell label="Total Courses" value={`${program.displayCourseCount || program.courses.length} Courses`} />
          </div>

          <SectionRule label="Who This Is For" />
          <ul className="space-y-2 mb-8" data-testid="rec-outcomes">
            {program.forWhom.map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <SectionRule label="Format" />
          <p className="text-sm text-slate-700 leading-relaxed mb-8">{program.format}</p>

          {startMonth && (
            <>
              <SectionRule label="Your desired start" />
              <p className="text-sm text-slate-900 font-medium mb-8" data-testid="rec-start-month">
                {startMonth}
              </p>
            </>
          )}

          <SectionRule label="Next Steps" />
          <div className="space-y-3">
            <Link to={applyHref} data-testid="rec-apply-btn">
              <Button size="lg" className="w-full bg-purple-900 hover:bg-purple-800 text-white rounded-full">
                Apply Now — $50 Application Fee
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to={detailHref} data-testid="rec-detail-btn">
              <Button variant="outline" className="w-full rounded-full">
                View Full Program Details
              </Button>
            </Link>
            <Button
              variant="ghost"
              className="w-full text-slate-600"
              onClick={() => { clearQuizState(); navigate("/university/quiz"); }}
              data-testid="rec-retake-btn"
            >
              <RefreshCcw className="w-4 h-4 mr-1" /> Retake the Quiz
            </Button>
            <Link to="/university#programs-of-study" data-testid="rec-browse-btn">
              <Button variant="ghost" className="w-full text-slate-600">
                Choose a Different Pathway
              </Button>
            </Link>
          </div>
        </main>
      </RecommendationShell>
    );
  }

  // CE recommendation
  const ceCatalog = ceChoice ? mapCEFriendlyToCatalog(ceChoice) : "Other Continuing Education Courses";
  const ceApplyHref = buildApplyUrl({
    quizKey: "CE",
    primaryProgram: ceCatalog,
    ceFriendly: ceChoice || "Not sure yet / Need guidance",
    startMonth,
    quizRecommendedProgram: `Continuing Education — ${ceChoice || "CE Courses"}`,
  });

  return (
    <RecommendationShell tint="ce">
      <Hero
        eyebrow="Your recommended pathway"
        level="Professional & Continuing Education"
        title="Continuing Education Courses for Beauty & Wellness Professionals"
        desc="Short hands-on and professional development courses designed for licensed beauty professionals, wellness practitioners, and students who want to expand their skills without enrolling in a full certification pathway."
        tint="ce"
      />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="rec-info-grid">
          <InfoCell label="Pathway" value="Continuing Education" />
          <InfoCell label="Format" value="Standalone Courses" />
          <InfoCell label="Clock Hours" value="Varies by Course" />
          <InfoCell label="Tuition" value="Course-Based Pricing" />
        </div>

        <SectionRule label="Pick the CE course you're most interested in" />
        <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="ce-course-list">
          {CE_COURSES.map((c) => {
            const tid = `ce-card-${c.title.replace(/\W+/g, "-").toLowerCase()}`;
            const on = ceChoice === c.title;
            return (
              <button
                key={c.title}
                type="button"
                onClick={() => setCeChoice(c.title)}
                data-testid={tid}
                className={`text-left rounded-2xl border bg-white p-4 transition ${
                  on ? "border-amber-500 ring-2 ring-amber-200" : "border-slate-200 hover:border-purple-400"
                }`}
              >
                <div className="font-display text-base text-slate-900">{c.title}</div>
                <div className="text-[10px] uppercase tracking-wide text-purple-900 font-semibold mt-1">
                  {c.hours}
                </div>
                <div className="text-xs text-slate-600 mt-2 leading-relaxed">{c.description}</div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setCeChoice("Not sure yet / Need guidance")}
            data-testid="ce-card-not-sure"
            className={`text-left rounded-2xl border bg-white p-4 transition ${
              ceChoice === "Not sure yet / Need guidance" ? "border-amber-500 ring-2 ring-amber-200" : "border-slate-200 hover:border-purple-400"
            }`}
          >
            <div className="font-display text-base text-slate-900">Not sure yet</div>
            <div className="text-xs text-slate-600 mt-2 leading-relaxed">
              I need admissions guidance to choose a CE course.
            </div>
          </button>
        </div>

        {startMonth && (
          <>
            <SectionRule label="Your desired start" />
            <p className="text-sm text-slate-900 font-medium mb-8" data-testid="rec-start-month">{startMonth}</p>
          </>
        )}

        <SectionRule label="Next Steps" />
        <div className="space-y-3">
          <Link to={ceApplyHref} data-testid="rec-apply-btn">
            <Button size="lg" className="w-full bg-purple-900 hover:bg-purple-800 text-white rounded-full">
              Continue to Application — $50 Application Fee
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
          <Button
            variant="ghost"
            className="w-full text-slate-600"
            onClick={() => { clearQuizState(); navigate("/university/quiz"); }}
            data-testid="rec-retake-btn"
          >
            <RefreshCcw className="w-4 h-4 mr-1" /> Retake the Quiz
          </Button>
          <Link to="/university#programs-of-study" data-testid="rec-browse-btn">
            <Button variant="ghost" className="w-full text-slate-600">
              Choose a Different Pathway
            </Button>
          </Link>
        </div>
      </main>
    </RecommendationShell>
  );
}

function RecommendationShell({ children, tint }) {
  const bg = tint === "ce" ? "bg-gradient-to-b from-amber-50 via-[#faf7f5] to-white" : "bg-[#faf7f5]";
  return (
    <div className={`min-h-screen ${bg}`} data-testid="university-recommendation">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/university" className="flex items-center gap-3">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
            <span className="font-display text-sm text-slate-700">KISSED University · Recommendation</span>
          </Link>
        </div>
      </header>
      {children}
    </div>
  );
}

function Hero({ eyebrow, level, title, desc, tint }) {
  const bg = tint === "ce"
    ? "bg-gradient-to-br from-amber-100/60 via-white to-amber-50/40"
    : "bg-gradient-to-br from-purple-100/60 via-white to-purple-50/40";
  return (
    <section className={`border-b border-slate-200 ${bg} px-6 py-12`}>
      <div className="max-w-3xl mx-auto text-center">
        <span className="inline-block rounded-full border border-purple-300 bg-white px-4 py-1 text-[10px] uppercase tracking-[0.18em] text-purple-900 font-semibold mb-4">
          {eyebrow}
        </span>
        <div className="text-xs uppercase tracking-[0.14em] text-purple-900 mb-2 font-semibold flex items-center justify-center gap-2">
          <GraduationCap className="w-4 h-4" /> {level}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-slate-900 leading-tight" data-testid="rec-title">
          {title}
        </h1>
        <p className="mt-4 text-sm text-slate-700 max-w-2xl mx-auto leading-relaxed" data-testid="rec-desc">
          {desc}
        </p>
      </div>
    </section>
  );
}

function InfoCell({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="text-[10px] uppercase tracking-wide text-purple-900 font-semibold">{label}</div>
      <div className="text-sm font-medium text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function SectionRule({ label }) {
  return (
    <div className="flex items-center gap-3 my-5">
      <span className="text-[10px] uppercase tracking-[0.18em] text-purple-900 font-semibold whitespace-nowrap">
        {label}
      </span>
      <span className="flex-1 h-px bg-slate-200" />
    </div>
  );
}
